import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router';
import { ColumnsType } from 'antd/es/table';

import { Button, Footer, TitleBar } from '../../../../../components';
import TableV3 from '../../../../../components/generic/Table/TableV3';
import QueryErrorBoundary from '../../../../../components/error-handling';
import TitleUpdater from '../../../../../components/common/TitleUpdater';

import { CompletionPercentage } from '../../../components';

import { tableSortingMethods } from '../../../../../utils';

import { FETCH_BUSINESS_FUNCTIONS_WORKAROUNDS } from './queries';
import {
  BusinessFunctionStats,
  GetBusinessFunctionWorkaroundStatsResponse,
} from '../../../../../shared/types/business-functions-workarounds';

const WorkaroundsBusinessFunctionsList = () => {
  const navigate = useNavigate();

  const {
    data: workaroundsBusinessFunctionsData,
    loading: workaroundsBusinessFunctionsLoading,
    error: workaroundsBusinessFunctionsError,
  } = useQuery<GetBusinessFunctionWorkaroundStatsResponse>(
    FETCH_BUSINESS_FUNCTIONS_WORKAROUNDS,
  );

  const tableData =
    workaroundsBusinessFunctionsData?.getBusinessFunctionWorkaroundStats ?? [];

  const columns: ColumnsType<BusinessFunctionStats> = [
    {
      title: 'Business Function',
      dataIndex: 'name',
      key: 'name',
      width: 350,
      defaultSortOrder: 'ascend',
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (name: string) => (
        <span className="custom-table--column ml-10">{name}</span>
      ),
    },
    {
      title: '# of Applications Tagged',
      dataIndex: 'totalTaggedApplications',
      key: 'totalTaggedApplications',
      align: 'center',
      width: 150,
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.totalTaggedApplications,
          b.totalTaggedApplications,
        ),
      render: (totalTaggedApplications: string) => (
        <span className="custom-table--column">{totalTaggedApplications}</span>
      ),
    },
    {
      title: (
        <span>
          Workaround
          <br />
          Completion
        </span>
      ),
      key: 'completionPercentage',
      dataIndex: 'completionPercentage',
      align: 'center',
      width: 150,
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.completionPercentage,
          b.completionPercentage,
        ),
      render: (completionPercentage: number) => (
        <CompletionPercentage percent={completionPercentage} colorize />
      ),
    },
  ];

  const extendRowActions = (record: BusinessFunctionStats) => {
    return (
      <Button
        type="primary-rounded"
        className="w-full text-sm font-semibold whitespace-nowrap min-w-max"
        onClick={() =>
          navigate(
            `/cyber/enterprise-level/application-workarounds/business-functions/${record.businessFunctionId}/summary`,
          )
        }
      >
        Add Workarounds
      </Button>
    );
  };

  return (
    <div>
      <TitleUpdater title="Application Workarounds" />
      <TitleBar
        title="Enterprise Business Function Workarounds"
        subtitle={`Business Functions`}
        brief="Below is a list of Business Functions at the Enterprise Level and the number of applications that are tagged to each function."
      />
      <QueryErrorBoundary error={[workaroundsBusinessFunctionsError]}>
        <div className="mt-6">
          <TableV3
            data={tableData}
            columns={columns}
            loading={workaroundsBusinessFunctionsLoading}
            extendActions={extendRowActions}
            rowKey="businessFunctionId"
            tableSize="small"
            nonSearchableColumns={[
              'businessFunctionId',
              'applicationsWithWorkarounds',
            ]}
          />
        </div>
      </QueryErrorBoundary>
      <Footer backPath="/cyber/enterprise-level/application-workarounds" />
    </div>
  );
};

export default WorkaroundsBusinessFunctionsList;
